import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import experienciaVideo from '../assets/videos/experiencia-1.mp4';
import brindisVideo from '../assets/videos/brindis-1.mp4';
import charlaVideo from '../assets/videos/charla-1.mp4';
import charlaDosVideo from '../assets/videos/charla-2.mp4';
import cataVideo from '../assets/videos/cata-1.mp4';
import luigiLogo from '../assets/bodegas/luigi.png';
import sottanoLogo from '../assets/bodegas/sottano.png';
import productoUno from '../assets/producto-1.jpg';
import productoDos from '../assets/producto-2.jpg';
import productoTres from '../assets/producto-3.jpg';
import mesaUno from '../assets/mesas/mesa-1.jpg';
import mesaDos from '../assets/mesas/mesa-2.jpg';
import mesaTres from '../assets/mesas/mesa-3.jpg';
import mesaCuatro from '../assets/mesas/mesa-4.jpg';

const Home: React.FC = () => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentMesaIndex, setCurrentMesaIndex] = useState(0);

  const videos = [experienciaVideo, brindisVideo, charlaVideo, charlaDosVideo, cataVideo];
  const mesas = [mesaUno, mesaDos, mesaTres, mesaCuatro];

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % videos.length);
    }, 3000);

    return () => clearTimeout(timer);
  }, [currentVideoIndex, videos.length]);

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video) return;

    const maxStart = Math.max(0, video.duration - 3);
    const randomStart = maxStart > 0 ? Math.random() * maxStart : 0;

    video.currentTime = randomStart;
    video.playbackRate = 0.7;

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => undefined);
    }
  };

  const handleMesaPrev = () => {
    setCurrentMesaIndex((prevIndex) => (prevIndex - 1 + mesas.length) % mesas.length);
  };

  const handleMesaNext = () => {
    setCurrentMesaIndex((prevIndex) => (prevIndex + 1) % mesas.length);
  };

  return (
    <div className="home-page">
      <section className="home-hero">
        <div className="home-hero__media">
          <video
            key={currentVideoIndex}
            ref={videoRef}
            className="home-hero__video"
            autoPlay
            muted
            playsInline
            preload="metadata"
            onLoadedMetadata={handleLoadedMetadata}
          >
            <source src={videos[currentVideoIndex]} type="video/mp4" />
          </video>
        </div>
        <div className="home-hero__overlay" />
        <div className="home-hero__content">
          <h1 className="home-hero__title home-hero__title--light">Experiencias únicas, pensadas para vos</h1>
          <p className="home-hero__subtitle">
            Somos tu club de vinos. Desde acercar las mejores etiquetas a tu hogar hasta crear momentos únicos, convertimos cada etapa en experiencias inolvidables.
          </p>
          <div className="home-hero__actions">
            <Link to="/seleccion" className="home-cta home-cta--primary home-cta--soft">
              Conoce nuestra selección
            </Link>
            <Link to="/quienes-somos" className="home-cta home-cta--ghost">
              El Club
            </Link>
          </div>
          <div className="home-hero__logos">
            <img src={luigiLogo} alt="Luigi Bosca" className="home-hero__logo" />
            <img src={sottanoLogo} alt="Bodega Sottano" className="home-hero__logo" />
          </div>
        </div>
        <div className="home-hero__carousel">
          <div className="home-hero__carousel-frame" aria-hidden="true">
            <div className="home-hero__carousel-track">
              {[...mesas, ...mesas].map((mesa, index) => (
                <div className="home-hero__carousel-slide" key={`${mesa}-${index}`}>
                  <img src={mesa} alt="" />
                </div>
              ))}
            </div>
            <div className="home-hero__carousel-fade">
              {mesas.map((mesa, index) => (
                <img
                  key={mesa}
                  src={mesa}
                  alt=""
                  className={`home-hero__carousel-fade-image ${
                    index === currentMesaIndex ? 'is-active' : ''
                  }`}
                />
              ))}
            </div>
            <div className="home-hero__carousel-controls">
              <button type="button" className="home-hero__carousel-btn" onClick={handleMesaPrev}>
                <i className="fas fa-chevron-left" aria-hidden="true"></i>
              </button>
              <button type="button" className="home-hero__carousel-btn" onClick={handleMesaNext}>
                <i className="fas fa-chevron-right" aria-hidden="true"></i>
              </button>
            </div>
          </div>
          <div className="home-hero__carousel-dots" aria-hidden="true">
            {mesas.map((_, index) => (
              <button
                key={`mesa-dot-${index}`}
                type="button"
                className={`home-hero__carousel-dot ${
                  index === currentMesaIndex ? 'is-active' : ''
                }`}
                onClick={() => setCurrentMesaIndex(index)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="home-split">
        <div className="home-split__row">
          <div className="home-split__text">
            <h2>Una selección amplia para cada paladar</h2>
            <p>
              Trabajamos con bodegas y etiquetas de distintos estilos para que siempre encuentres
              un vino que se adapte a vos. Desde clasicos reconocidos hasta joyas de producción
              limitada, nuestro origen como distribuidora nos vuelve expertos en conseguir variedad a precios únicos.
            </p>
            <Link to="/seleccion" className="home-split__cta">
              Ver selección
            </Link>
          </div>
          <div className="home-split__image">
            <img src={productoUno} alt="Seleccion de productos" />
          </div>
        </div>

        <div className="home-split__row">
          <div className="home-split__image">
            <img src={productoDos} alt="Experiencias personalizadas" />
          </div>
          <div className="home-split__text">
            <h2>Experiencias personalizadas</h2>
            <p>
              Diseñamos propuestas a medida para eventos privados, cumpleaños, regalos corporativos o
              celebraciones especiales. Nos ocupamos de cada detalle para que vivas una experiencia
              única, sin complicaciones.
            </p>
            <a
              className="home-split__cta home-split__cta--whatsapp"
              href="https://api.whatsapp.com/message/5LUL6QCX55ICO1?autoload=1&app_absent=0"
              target="_blank"
              rel="noreferrer"
            >
              <i className="fab fa-whatsapp" aria-hidden="true"></i>
              Conocé más
            </a>
          </div>
        </div>

        <div className="home-split__row">
          <div className="home-split__text">
            <h2>Regalá momentos</h2>
            <p>
              Somos referentes en estucheria y regaleria del mundo del vino, con propuestas
              cuidadas para celebrar, agradecer o sorprender con estilo.
            </p>
            <a
              className="home-split__cta home-split__cta--whatsapp"
              href="https://api.whatsapp.com/message/5LUL6QCX55ICO1?autoload=1&app_absent=0"
              target="_blank"
              rel="noreferrer"
            >
              <i className="fab fa-whatsapp" aria-hidden="true"></i>
              Catálogo exclusivo
            </a>
          </div>
          <div className="home-split__image">
            <img src={productoTres} alt="Regalos y estucheria" />
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
